import * as THREE from 'three'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { Heightmap } from '../world/heightmap'
import { Kula, Mreza, NIVOI } from './mreza'

export const BOJE_IGRACA = ['#f0a83c', '#4aa3e8', '#b47ae0', '#e6e0d2']
export const IMENA_IGRACA = ['Žuti', 'Plavi', 'Ljubičasti', 'Beli']

const BOJA_PREKID = new THREE.Color('#e0523f')
const BOJA_PREGLED = new THREE.Color('#79f0b4')

/**
 * Sve što se od mreže vidi na ekranu. Logika je u `mreza.ts` — ovde se samo
 * crta ono što ona kaže.
 */
export class Prikaz {
  readonly group = new THREE.Group()

  private kuleGrupa = new THREE.Group()
  private duh = new THREE.Group()
  private korenovi: THREE.Object3D[] = []
  private animacije = new Map<number, number>()

  private linijeVeza: LineSegments2
  private linijePrekida: LineSegments2
  private linijePregleda: LineSegments2
  private materijali: LineMaterial[] = []

  private krugDometa: THREE.Line
  private markerPrepreke: THREE.Mesh

  private geo: {
    postolje: THREE.CylinderGeometry
    stub: THREE.CylinderGeometry
    ruka: THREE.BoxGeometry
    kazaljka: THREE.BoxGeometry
    lampa: THREE.SphereGeometry
    marker: THREE.OctahedronGeometry
  }
  private mat: {
    telo: THREE.MeshStandardMaterial
    stub: THREE.MeshStandardMaterial
    lampa: THREE.MeshBasicMaterial[]
    igrac: THREE.MeshStandardMaterial[]
    duh: THREE.MeshStandardMaterial
    duhLose: THREE.MeshStandardMaterial
    marker: THREE.MeshBasicMaterial
    krug: THREE.LineBasicMaterial
  }

  private skala: number
  private vreme = 0

  constructor(private hm: Heightmap, private mreza: Mreza) {
    this.skala = hm.size / 1400
    const s = this.skala

    // Kule su namerno krupnije nego što bi bile u razmeri — one su figure na
    // tabli, a ne zgrade. Ako su u razmeri, sa visine se ne vide uopšte.
    this.geo = {
      postolje: new THREE.CylinderGeometry(7.5 * s, 9.5 * s, 3.4 * s, 10),
      stub: new THREE.CylinderGeometry(1.9 * s, 2.9 * s, 1, 6),
      ruka: new THREE.BoxGeometry(23 * s, 2.4 * s, 2.4 * s),
      kazaljka: new THREE.BoxGeometry(8.6 * s, 2 * s, 2 * s),
      lampa: new THREE.SphereGeometry(2.3 * s, 10, 8),
      marker: new THREE.OctahedronGeometry(4.4 * s, 0),
    }

    this.mat = {
      telo: new THREE.MeshStandardMaterial({ color: '#23272c', roughness: 0.74 }),
      // Stub je svetao da se kula odvoji od tamne padine iza sebe.
      stub: new THREE.MeshStandardMaterial({ color: '#ddd7c9', roughness: 0.55 }),
      lampa: BOJE_IGRACA.map((b) => new THREE.MeshBasicMaterial({ color: b })),
      igrac: BOJE_IGRACA.map(
        (b) =>
          new THREE.MeshStandardMaterial({
            color: b,
            roughness: 0.42,
            emissive: new THREE.Color(b).multiplyScalar(0.16),
          }),
      ),
      duh: new THREE.MeshStandardMaterial({
        color: '#9ef0c4',
        transparent: true,
        opacity: 0.45,
        roughness: 0.5,
        depthWrite: false,
      }),
      duhLose: new THREE.MeshStandardMaterial({
        color: '#e0523f',
        transparent: true,
        opacity: 0.4,
        roughness: 0.5,
        depthWrite: false,
      }),
      marker: new THREE.MeshBasicMaterial({ color: '#e0523f' }),
      krug: new THREE.LineBasicMaterial({ color: '#9ef0c4', transparent: true, opacity: 0.35 }),
    }

    this.linijeVeza = this.napraviLinije(3.1, true)
    this.linijePrekida = this.napraviLinije(1.7, false)
    this.linijePregleda = this.napraviLinije(2.4, true)

    const krugGeo = new THREE.BufferGeometry()
    krugGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * 97), 3))
    this.krugDometa = new THREE.Line(krugGeo, this.mat.krug)
    this.krugDometa.visible = false
    this.krugDometa.frustumCulled = false

    this.markerPrepreke = new THREE.Mesh(this.geo.marker, this.mat.marker)
    this.markerPrepreke.visible = false

    this.group.name = 'mreza'
    this.group.add(
      this.kuleGrupa,
      this.duh,
      this.linijeVeza,
      this.linijePrekida,
      this.linijePregleda,
      this.krugDometa,
      this.markerPrepreke,
    )
    this.duh.visible = false
  }

  private napraviLinije(sirina: number, isprekidano: boolean): LineSegments2 {
    const mat = new LineMaterial({
      vertexColors: true,
      linewidth: sirina,
      transparent: true,
      opacity: 0.95,
      dashed: isprekidano,
      dashSize: 13 * this.skala,
      gapSize: 5 * this.skala,
    })
    mat.resolution.set(innerWidth, innerHeight)
    this.materijali.push(mat)
    const linije = new LineSegments2(new LineSegmentsGeometry(), mat)
    linije.frustumCulled = false
    linije.renderOrder = 5
    return linije
  }

  postaviRezoluciju(w: number, h: number): void {
    for (const m of this.materijali) m.resolution.set(w, h)
  }

  // --- kule ---------------------------------------------------------------

  /** Sklapa jednu kulu: postolje, stub i Chappeove semaforske ruke. */
  private napraviKulu(k: Kula, prozirno: boolean): THREE.Group {
    const s = this.skala
    const g = new THREE.Group()
    const bojaMat = prozirno ? this.mat.duh : this.mat.igrac[k.igrac]
    const teloMat = prozirno ? this.mat.duh : this.mat.telo
    const stubMat = prozirno ? this.mat.duh : this.mat.stub

    const postolje = new THREE.Mesh(this.geo.postolje, teloMat)
    postolje.position.y = 1.3 * s
    postolje.castShadow = !prozirno
    postolje.receiveShadow = !prozirno
    g.add(postolje)

    const visinaStuba = NIVOI[k.nivo].visina * s
    const stub = new THREE.Mesh(this.geo.stub, stubMat)
    stub.scale.y = visinaStuba
    stub.position.y = 2.4 * s + visinaStuba / 2
    stub.castShadow = !prozirno
    g.add(stub)

    // Semaforske ruke — regulator i dve kazaljke na krajevima.
    const glava = new THREE.Group()
    glava.position.y = 2.4 * s + visinaStuba
    glava.name = 'glava'

    const ruka = new THREE.Mesh(this.geo.ruka, bojaMat)
    ruka.castShadow = !prozirno
    glava.add(ruka)

    for (const strana of [-1, 1]) {
      const kaz = new THREE.Mesh(this.geo.kazaljka, bojaMat)
      kaz.position.set(strana * 11.5 * s, 0, 0)
      kaz.rotation.z = strana * 0.9
      kaz.castShadow = !prozirno
      glava.add(kaz)
    }
    g.add(glava)

    if (!prozirno) {
      // Svetlo na vrhu — jedina tačka koja se vidi i kad se gleda sa visine.
      const lampa = new THREE.Mesh(this.geo.lampa, this.mat.lampa[k.igrac])
      // Lokalno u odnosu na glavu, koja je već podignuta na vrh stuba.
      lampa.position.y = 3.6 * s
      glava.add(lampa)
    }

    g.position.set(k.x, k.teren, k.z)
    return g
  }

  osveziKule(): void {
    for (const d of this.kuleGrupa.children.slice()) {
      this.kuleGrupa.remove(d)
    }
    this.korenovi = []

    for (const k of this.mreza.kule) {
      const g = this.napraviKulu(k, false)
      g.userData.kulaId = k.id
      if (!this.animacije.has(k.id)) this.animacije.set(k.id, 0)
      this.kuleGrupa.add(g)
      this.korenovi.push(g)
    }

    for (const id of [...this.animacije.keys()]) {
      if (!this.mreza.poId(id)) this.animacije.delete(id)
    }
  }

  // --- veze ---------------------------------------------------------------

  osveziVeze(izabrana: number | null): void {
    const poz: number[] = []
    const boje: number[] = []
    const pozPrekid: number[] = []
    const bojePrekid: number[] = []
    const c = new THREE.Color()

    for (const v of this.mreza.veze) {
      const a = this.mreza.poId(v.a)
      const b = this.mreza.poId(v.b)
      if (!a || !b) continue

      if (v.vidi) {
        // Veza između dva igrača se crta bledo — to je tuđa kula kroz koju
        // bi kasnije morao da platiš relej, ne tvoja linija.
        const svoja = a.igrac === b.igrac
        c.set(BOJE_IGRACA[a.igrac])
        if (!svoja) c.lerp(new THREE.Color('#8b96a0'), 0.65)
        poz.push(a.x, a.vrh, a.z, b.x, b.vrh, b.z)
        boje.push(c.r, c.g, c.b, c.r, c.g, c.b)
      } else if (izabrana === v.a || izabrana === v.b) {
        pozPrekid.push(a.x, a.vrh, a.z, b.x, b.vrh, b.z)
        bojePrekid.push(
          BOJA_PREKID.r, BOJA_PREKID.g, BOJA_PREKID.b,
          BOJA_PREKID.r, BOJA_PREKID.g, BOJA_PREKID.b,
        )
      }
    }

    this.napuni(this.linijeVeza, poz, boje)
    this.napuni(this.linijePrekida, pozPrekid, bojePrekid)

    // Marker prve prepreke za izabranu kulu — pokazuje šta tačno smeta.
    this.markerPrepreke.visible = false
    if (izabrana !== null) {
      const prva = this.mreza.veze.find(
        (v) => !v.vidi && v.prepreka && (v.a === izabrana || v.b === izabrana),
      )
      if (prva?.prepreka) {
        this.markerPrepreke.position.set(prva.prepreka.x, prva.prepreka.y + 3 * this.skala, prva.prepreka.z)
        this.markerPrepreke.visible = true
      }
    }

    this.krugDometa.visible = false
    if (izabrana !== null) {
      const k = this.mreza.poId(izabrana)
      if (k) this.crtajKrug(k.x, k.z, k.domet)
    }
  }

  private napuni(linije: LineSegments2, poz: number[], boje: number[]): void {
    linije.visible = poz.length > 0
    if (!poz.length) return
    const geo = new LineSegmentsGeometry()
    geo.setPositions(poz)
    geo.setColors(boje)
    linije.geometry.dispose()
    linije.geometry = geo
    linije.computeLineDistances()
  }

  /** Krug dometa se ne crta u vazduhu nego prati teren, pa se odmah čita. */
  private crtajKrug(x: number, z: number, r: number): void {
    const poz = this.krugDometa.geometry.getAttribute('position') as THREE.BufferAttribute
    const n = poz.count
    for (let i = 0; i < n; i++) {
      const a = (i / (n - 1)) * Math.PI * 2
      const px = x + Math.cos(a) * r
      const pz = z + Math.sin(a) * r
      poz.setXYZ(i, px, Math.max(0, this.hm.sample(px, pz)) + 1.5 * this.skala, pz)
    }
    poz.needsUpdate = true
    this.krugDometa.geometry.computeBoundingSphere()
    this.krugDometa.visible = true
  }

  // --- pregled pod kursorom -----------------------------------------------

  sakrijPregled(): void {
    this.duh.visible = false
    this.linijePregleda.visible = false
  }

  /**
   * Duh kule pod kursorom i linije ka svemu što bi odatle videlo.
   * Ovo je centralni potez cele igračke — pre nego što spustiš kulu, vidiš
   * šta dobijaš i šta ti brdo pojede.
   */
  prikaziPregled(x: number, z: number, nivo: number, igrac: number): {
    moze: boolean
    razlog?: string
    veza: number
    prekid: number
  } {
    const { x: mx, z: mz, predlog, vidi, nevidi } = this.mreza.pregled(x, z, nivo)

    for (const d of this.duh.children.slice()) this.duh.remove(d)
    const kula: Kula = {
      id: -1, igrac, nivo, x: mx, z: mz,
      teren: predlog.teren, vrh: predlog.vrh, domet: predlog.domet,
    }
    const g = this.napraviKulu(kula, true)
    g.traverse((o) => {
      if (o instanceof THREE.Mesh) o.material = predlog.moze ? this.mat.duh : this.mat.duhLose
    })
    this.duh.add(g)
    this.duh.visible = true

    const poz: number[] = []
    const boje: number[] = []
    const dodaj = (k: Kula, c: THREE.Color) => {
      poz.push(mx, predlog.vrh, mz, k.x, k.vrh, k.z)
      boje.push(c.r, c.g, c.b, c.r, c.g, c.b)
    }
    if (predlog.moze) {
      for (const k of vidi) dodaj(k, BOJA_PREGLED)
      for (const k of nevidi) dodaj(k, BOJA_PREKID)
    }
    this.napuni(this.linijePregleda, poz, boje)

    if (predlog.moze) this.crtajKrug(mx, mz, predlog.domet)
    else this.krugDometa.visible = false

    return { moze: predlog.moze, razlog: predlog.razlog, veza: vidi.length, prekid: nevidi.length }
  }

  // --- ostalo -------------------------------------------------------------

  /** Koja je kula pod zrakom, ako ijedna. */
  pogodi(raycaster: THREE.Raycaster): number | null {
    const preseci = raycaster.intersectObjects(this.korenovi, true)
    for (const p of preseci) {
      let o: THREE.Object3D | null = p.object
      while (o) {
        if (o.userData.kulaId !== undefined) return o.userData.kulaId as number
        o = o.parent
      }
    }
    return null
  }

  update(dt: number, udaljenostKamere: number): void {
    this.vreme += dt

    // Sa visine bi kule u razmeri bile nekoliko piksela. Blago rastu kad se
    // kamera udalji, kao figure na tabli koje se ne smanjuju sa pogledom.
    const referenca = this.hm.size * 0.3
    const rast = Math.max(1, udaljenostKamere / referenca)
    // Stub se širi umereno da ne ispadne spljošten; svetlo na vrhu nosi
    // čitljivost sa velike visine, ono sme i duplo.
    const uvecanje = Math.min(1.4, rast)
    const uvecanjeLampe = Math.min(2.4, rast)

    // Signal koji putuje linijom — pomeranje isprekidanosti.
    for (const m of this.materijali) {
      if (m.dashed) m.dashOffset = -this.vreme * 26 * this.skala
    }

    // Kula izraste sa blagim prebačajem kad se postavi.
    for (const g of this.kuleGrupa.children) {
      const id = g.userData.kulaId as number
      let t = this.animacije.get(id) ?? 1
      if (t < 1) {
        t = Math.min(1, t + dt * 3.4)
        this.animacije.set(id, t)
      }
      const e = 1 - Math.pow(1 - t, 3)
      // Visina se NE uvećava — snop se kači na stvarni vrh kule, pa bi se
      // inače linija zabola u sredinu stuba. Širi se samo u ravni.
      g.scale.y = e * (1 + 0.12 * Math.sin(t * Math.PI))
      g.scale.x = g.scale.z = uvecanje * (0.6 + 0.4 * e)

      const lampa = g.getObjectByName('lampa')
      if (lampa) lampa.scale.setScalar(uvecanjeLampe)

      // Semaforske ruke se lagano pomeraju, svaka svojim ritmom.
      const glava = g.getObjectByName('glava')
      if (glava) glava.rotation.y = Math.sin(this.vreme * 0.5 + id * 1.7) * 0.55
    }

    // Duh prati istu širinu kao postavljene kule.
    const duhKula = this.duh.children[0]
    if (duhKula) duhKula.scale.set(uvecanje, 1, uvecanje)

    const p = this.markerPrepreke
    if (p.visible) {
      p.rotation.y = this.vreme * 1.4
      p.position.y += Math.sin(this.vreme * 3) * 0.04 * this.skala
    }
  }

  dispose(): void {
    for (const g of Object.values(this.geo)) g.dispose()
    for (const m of Object.values(this.mat)) {
      if (Array.isArray(m)) m.forEach((x) => x.dispose())
      else m.dispose()
    }
    for (const m of this.materijali) m.dispose()
    this.linijeVeza.geometry.dispose()
    this.linijePrekida.geometry.dispose()
    this.linijePregleda.geometry.dispose()
    this.krugDometa.geometry.dispose()
  }
}
